import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Store, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import { UpdateShopForm } from './update-shop-form.tsx';
import { UpdateCustomShopForm } from './update-custom-shop-form.tsx';
import { UpdateRewardShopForm } from './update-reward-shop-form.tsx';
import { ShopShopReadModelType } from '@r/api/generated/client.schemas.ts';
import type {
  ShopShopReadModel,
  ShopWorkspaceShopReadModel
} from '@r/api/generated/client.schemas.ts';

type Shop = Omit<ShopShopReadModel, 'shopWorkspace'> & {
  shopWorkspace: ShopWorkspaceShopReadModel;
};

type ShopDetailsGridProps = {
  shop: Shop;
  isEditOpen: boolean;
  onEditOpenChange: (open: boolean) => void;
  canEdit: boolean;
  onLogoPreviewChange?: (preview?: string) => void;
  /** Layout boutique reward (programme à paliers) : champs et formulaire limités. */
  rewardStepLayout?: boolean;
};

export function ShopDetailsGrid({
  shop,
  isEditOpen,
  onEditOpenChange,
  canEdit,
  onLogoPreviewChange,
  rewardStepLayout = false
}: ShopDetailsGridProps) {
  const { t } = useTranslation();
  const { shopWorkspace } = shop;

  return (
    <Card className="w-full">
      <CardHeader className="p-10 pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
            <Store />
            <p className="text-xl font-bold">{t('admin.shop.title')}</p>
          </CardTitle>

          {canEdit && (
            <Dialog open={isEditOpen} onOpenChange={onEditOpenChange}>
              <DialogTrigger asChild>
                <Button variant="link" className="px-0 h-auto text-primary">
                  <Pencil />
                  {t('common.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="overflow-hidden p-0 max-w-2xl">
                <div className="p-10 max-h-[90vh] overflow-y-auto scrollable">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">{t('admin.shop.title')}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    {rewardStepLayout ? (
                      <UpdateRewardShopForm
                        {...shop}
                        shopWorkspace={shopWorkspace}
                        onSuccess={() => onEditOpenChange(false)}
                      />
                    ) : shop.type === ShopShopReadModelType.custom ? (
                      <UpdateCustomShopForm
                        {...shop}
                        shopWorkspace={shopWorkspace}
                        onLogoPreviewChange={onLogoPreviewChange}
                        onSuccess={() => onEditOpenChange(false)}
                      />
                    ) : (
                      <UpdateShopForm
                        {...shop}
                        shopWorkspace={shopWorkspace}
                        onSuccess={() => onEditOpenChange(false)}
                      />
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-10 pt-0 space-y-2">
        <p className="font-semibold">{t('admin.shops.update.form.section.personalization')}</p>
        <div className="h-px bg-border" />
        <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-y-4 text-base">
          <p className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.display.name.label')}
          </p>
          <p className="text-sm">{shop.displayName || '-'}</p>

          <p className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.description.label')}
          </p>
          <p className="min-w-0 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
            {shop.description || '-'}
          </p>

          {rewardStepLayout && (
            <>
              <p className="text-sm font-medium text-muted-foreground">
                {t('admin.shops.update.form.operational.contact.label')}
              </p>
              <p className="text-sm">{shop.operationalContact || '-'}</p>

              <p className="text-sm font-medium text-muted-foreground">
                {t('admin.shops.update.form.technical.contact.label')}
              </p>
              <p className="text-sm">{shop.technicalContact || '-'}</p>
            </>
          )}

          {!rewardStepLayout && (
            <>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.shop.opening.date')}
              </p>
              <p className="text-sm">
                {shopWorkspace.shopOpeningDate
                  ? format(new Date(shopWorkspace.shopOpeningDate), 'dd/MM/yyyy')
                  : '-'}
              </p>

              <p className="text-sm font-medium text-muted-foreground">
                {t('label.shop.closing.date')}
              </p>
              <p className="text-sm">
                {shopWorkspace.shopClosingDate
                  ? format(new Date(shopWorkspace.shopClosingDate), 'dd/MM/yyyy')
                  : '-'}
              </p>

              <p className="text-sm font-medium text-muted-foreground">
                {t('form.shop.min.point')}
              </p>
              <p className="text-sm">{shopWorkspace.minPoint ?? '-'}</p>

              <p className="text-sm font-medium text-muted-foreground">
                {t('form.coefficient.label')}
              </p>
              <p className="text-sm">{shopWorkspace.coefficient ?? '-'}</p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
